import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../shared/components/MUI/button/Button';
import { useAppointmentContext, useUserContext } from '../../../shared/contexts';
import { HomeAppointmentsPage } from './HomeAppointmentsPage';
import { AlertTypes, appointmentService, feedback, IAppointmentView, IUpdateAppointment } from '../../../shared/services';
import { Api } from '../../../shared/services/api/axios-config';
import { setCsrfRequestToken } from '../../../shared/services/api/axios-config/interceptors';
import { GlassCard } from '../../../shared/components';
import { HomePackages } from './HomePackages';

// Ordena do mais recente pro mais antigo
const sortByDateDesc = (a: IAppointmentView, b: IAppointmentView) =>
  new Date(b.date).getTime() - new Date(a.date).getTime();

const sortByCanceledAtDesc = (a: IAppointmentView, b: IAppointmentView) => {
  const aTime = a.canceledAt ? new Date(a.canceledAt).getTime() : 0;
  const bTime = b.canceledAt ? new Date(b.canceledAt).getTime() : 0;
  return bTime - aTime || sortByDateDesc(a, b);
};

const sortByDateAsc = (a: IAppointmentView, b: IAppointmentView) =>
  new Date(a.date).getTime() - new Date(b.date).getTime();

enum HomeTabsEnum {
  Canceled = 0,
  Past = 1,
  Next = 2
}

interface IProps {
  appointments?: IAppointmentView[];
  refetch: () => void;
}

export const HomeAppointments: React.FC<IProps> = ({ appointments, refetch }) => {
  const [tabIndex, setTabIndex] = useState(HomeTabsEnum.Next);
  const [appointmentId, setAppointmentId] = useState<number>();
  const [cancelReason, setCancelReason] = useState('');
  const [openDialogCancelAppointment, setOpenDialogCancelAppointment] = useState(false);

  const navigate = useNavigate();

  const { validateConfirmedEmail } = useUserContext();

  const { setAppointment, setViaScheduler } = useAppointmentContext();

  const theme = useTheme();

  const colorDisabled = theme.palette.text.disabled;

  // Listas pré-filtradas e ordenadas (evita re-filter/re-sort a cada render)
  const canceledAppointments = useMemo(
    () => appointments?.filter(i => i.canceled).sort(sortByCanceledAtDesc) ?? [],
    [appointments]
  );
  const pastAppointments = useMemo(
    () => appointments?.filter(i => i.past && !i.canceled).sort(sortByDateDesc) ?? [],
    [appointments]
  );
  const upcomingAppointments = useMemo(
    () => appointments?.filter(i => !i.past && !i.canceled).sort(sortByDateAsc) ?? [],
    [appointments]
  );

  const handleClickCancel = useCallback((id?: number) => {
    if (id) {
      setAppointmentId(id);
      setCancelReason('');
      setOpenDialogCancelAppointment(true);
    }
  }, []);

  const { mutate: cancelAppointment, isLoading: isLoadingCancelAppointment } = useMutation(async (appointment: IUpdateAppointment) => {
    try {
      const tokenRes = await Api.get('/auth/antiforgery-token');
      if (tokenRes.data) setCsrfRequestToken(tokenRes.data as string);
    } catch {
      // best-effort
    }
    return appointmentService.update(appointment);
  }, {
    onSuccess: () => finishRequest('Agendamento cancelado', 'success'),
    onError: (error: Error) => finishRequest(error.message, 'error'),
  });

  const finishRequest = (message: string, feedbackType: AlertTypes) => {
    feedback(message, feedbackType);

    if (feedbackType === 'success') {
      refetch();
      handleCloseDialogCancelAppointment();
    }
  };

  const handleClickConfirmCancelAppointment = useCallback(async () => {
    const appointmentToCancel = appointments?.find(x => x.id === appointmentId);

    if (appointmentToCancel) {
      const submitData: IUpdateAppointment = {
        id: appointmentToCancel.id,
        canceled: true,
        cancelReason: cancelReason || undefined,
        date: appointmentToCancel.date,
        businessId: appointmentToCancel.businessId,
        businessEmployeeId: appointmentToCancel.businessEmployeeId,
        services: appointmentToCancel.services.map(x => ({ serviceId: x.id })),
        products: appointmentToCancel.products.map(x => ({ productId: x.id })),
        userId: appointmentToCancel.userId,
      };
      cancelAppointment(submitData);
    }
  }, [appointmentId, cancelReason, appointments]);

  const handleCloseDialogCancelAppointment = useCallback(() => {
    setOpenDialogCancelAppointment(false);
    setAppointmentId(undefined);
    setCancelReason('');
  }, []);

  const onClickSchedule = () => {
    if (!validateConfirmedEmail()) return;

    setViaScheduler(false);

    navigate('/appointment');
  };

  const onClickReschedule = (appointment: IAppointmentView) => {
    if (!validateConfirmedEmail()) return;

    setViaScheduler(false);
    setAppointment(appointment);
    navigate('/appointment');
  };

  const onChangeTab = useCallback((_: React.SyntheticEvent, index: number) => {
    setTabIndex(index);
  }, [tabIndex]);

  return (
    <Box display='flex' flexDirection='column' gap={3}>
      <GlassCard noPad>
        <Box display='flex' flexDirection='column' gap={2} padding={2}>
          <Typography variant='h4'>Agendamentos</Typography>

          <Tabs value={tabIndex} onChange={onChangeTab} data-testid='tabs-appointments'>
            <Tab label='Cancelados' data-testid='tab-canceled' />
            <Tab label='Passados' data-testid='tab-past' />
            <Tab label='Próximos' data-testid='tab-next' />
          </Tabs>

          {tabIndex === HomeTabsEnum.Canceled &&
            <>
              {canceledAppointments.length > 0 ?
                canceledAppointments.map((item) => (
                  <HomeAppointmentsPage
                    key={item.id}
                    appointment={item}
                    disabled
                    colorDisabled={colorDisabled}
                  />
                )) : (
                  <>
                    <Typography variant='h6' color={colorDisabled}>Você ainda não tem agendamentos canceledos!</Typography>
                  </>
                )}
            </>
          }

          {tabIndex === HomeTabsEnum.Past &&
            <>
              {pastAppointments.length > 0 ?
                pastAppointments.map((item) => (
                  <HomeAppointmentsPage
                    key={item.id}
                    appointment={item}
                    disabled
                    colorDisabled={colorDisabled}
                  >
                    <Box display='flex' justifyContent='center' gap={2}>
                      <Button
                        label='Reagendar'
                        onClick={() => onClickReschedule(item)}
                      />
                    </Box>
                  </HomeAppointmentsPage>
                )) : (
                  <>
                    <Typography variant='h6' color={colorDisabled}>Você ainda não tem agendamentos passados!</Typography>
                  </>
                )}
            </>
          }

          {tabIndex === HomeTabsEnum.Next &&
            <>
              {upcomingAppointments.length > 0 ?
                upcomingAppointments.map((item) => (
                  <HomeAppointmentsPage
                    key={item.id}
                    appointment={item}
                    disabled={false}
                    colorDisabled={colorDisabled}
                  >
                    <Box>
                      <Button label='Cancelar' onClick={() => handleClickCancel(item.id)} data-testid={`btn-cancel-appointment-${item.id}`} />
                    </Box>
                  </HomeAppointmentsPage>
                )) : (
                  <>
                    <Typography variant='h6' color={colorDisabled}>Você ainda não tem agendamentos!</Typography>
                  </>
                )}

              <Box>
                <Button
                  variant='contained'
                  label='Agendar'
                  onClick={onClickSchedule}
                  data-testid='btn-new-appointment'
                />
              </Box>
            </>
          }

          <Box sx={{ mt: 4 }}>
            <HomePackages />
          </Box>
        </Box>
      </GlassCard>

      {/* Diálogo de cancelamento com motivo */}
      <Dialog
        open={openDialogCancelAppointment}
        onClose={handleCloseDialogCancelAppointment}
        PaperProps={{
          sx: {
            animation: 'slideUp 0.25s ease',
            borderRadius: 4,
            background: theme.palette.mode === 'dark' ? '#161b22' : '#ffffff',
            border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
          },
        }}
        sx={{
          '& .MuiBackdrop-root': {
            backdropFilter: 'blur(4px)',
          },
        }}
      >
        <DialogContent sx={{ pb: 2, pt: 3, px: 3 }}>
          <Typography sx={{ fontWeight: 500, mb: 2 }}>
            Tem certeza que deseja cancelar este agendamento?
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Motivo do cancelamento (opcional)"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            variant="outlined"
            size="small"
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Box display='flex' justifyContent='center' flex={1} gap={1}>
            <Button
              borderRounded={false}
              color='secondary'
              label='Voltar'
              onClick={handleCloseDialogCancelAppointment}
            />
            <Button
              borderRounded={false}
              color='secondary'
              label='Confirmar'
              onClick={handleClickConfirmCancelAppointment}
              isLoading={isLoadingCancelAppointment}
            />
          </Box>
        </DialogActions>
      </Dialog>
    </Box >
  );
};
